import { useEffect, useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import LightBackground from '@/components/LightBackground';
import { 
  Users, 
  Settings,
  AlertTriangle,
  Loader2,
  RefreshCw,
  Plus,
  Eye,
  DollarSign,
  Database,
  MessageCircle,
  BookOpen,
  Sparkles,
  Rocket
} from 'lucide-react';
import CreateClientDialog from '@/components/CreateClientDialog';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';
import {
  fetchClients,
  createClient,
  Client,
  getActiveClients,
  getExpiringClients,
  getActiveLifetimeClients,
  getActiveMonthlyClients
} from '@/services/clientService';
import { mockClients } from '@/data/clientsData';
import { supabase } from '@/integrations/supabase/client';
import OwnerKPIs from '@/components/admin/OwnerKPIs';
import ActivityFeed from '@/components/admin/ActivityFeed';
import OwnerAlerts from '@/components/admin/OwnerAlerts';
import RevenueBreakdown, { StripeRevenueData } from '@/components/admin/RevenueBreakdown';

const COST_PER_CREDIT = 0.01;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { user, role, loading: authLoading, signOut } = useAuth();
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [useMockData, setUseMockData] = useState(false);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [revenuePeriod, setRevenuePeriod] = useState<'month' | '3months' | '6months' | 'year'>('month');
  const [aiUsageLogs, setAiUsageLogs] = useState<Array<{credits_used: number; date: string}>>([]);
  
  // Stripe live data
  const [stripeData, setStripeData] = useState<StripeRevenueData | null>(null);
  const [stripeLoading, setStripeLoading] = useState(false);
  const [stripeError, setStripeError] = useState<string | null>(null);

  useEffect(() => {
    if (!authLoading) {
      if (!user) navigate('/login');
      else if (role !== 'admin') {
        toast.error('Access denied. Admin privileges required.');
        navigate('/dashboard');
      }
    }
  }, [user, role, authLoading, navigate]);

  const loadClients = async () => {
    setLoading(true);
    try {
      const data = await fetchClients();
      if (data.length === 0) {
        setUseMockData(true);
        const convertedMockData: Client[] = mockClients.map((m) => ({
          id: m.id.toString(),
          user_id: null,
          name: m.name,
          email: m.email,
          plan: m.plan,
          plan_price: m.planPrice,
          status: m.status,
          paid: m.status !== 'expired',
          subscription_start: m.subscriptionStart,
          subscription_end: m.subscriptionEnd,
          last_active_at: m.lastActiveDate.toISOString(),
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          academy_progress: m.academyProgress.map((ap) => ({
            id: `${m.id}-${ap.academyId}`,
            client_id: m.id.toString(),
            academy_id: ap.academyId,
            academy_name: ap.academyName,
            completed_modules: ap.completedModules,
            total_modules: ap.totalModules,
          })),
        }));
        setClients(convertedMockData);
      } else {
        setUseMockData(false);
        setClients(data);
      }
    } catch (error) {
      console.error('Error loading clients:', error);
      toast.error('Failed to load clients. Using demo data.');
      setUseMockData(true);
    }
    setLoading(false);
  };

  const loadStripeRevenue = useCallback(async (period: string) => {
    setStripeLoading(true);
    setStripeError(null);
    try {
      const { data, error } = await supabase.functions.invoke('get-stripe-revenue', {
        body: { period },
      });
      if (error) throw new Error(error.message || 'Failed to fetch Stripe data');
      if (data?.error) throw new Error(data.error);
      setStripeData(data as StripeRevenueData);
    } catch (e: any) {
      console.error('Stripe revenue error:', e);
      setStripeError(e.message || 'Unknown error');
    }
    setStripeLoading(false);
  }, []);

  const loadFinancialData = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('ai_usage_log')
        .select('credits_used, tool_type, created_at')
        .not('tool_type', 'like', 'credit_purchase_%');

      if (error) throw error;

      const usageLogs = (data || []).map((l) => ({
        credits_used: Math.max(0, l.credits_used),
        date: l.created_at,
      }));
      setAiUsageLogs(usageLogs);
    } catch (e) {
      console.error('Error loading financial data:', e);
    }
  }, []);

  useEffect(() => {
    if (user && role === 'admin') {
      loadClients();
      loadFinancialData();
      loadStripeRevenue(revenuePeriod);
    }
  }, [user, role]);

  // Reload Stripe data when period changes
  useEffect(() => {
    if (user && role === 'admin') {
      loadStripeRevenue(revenuePeriod);
    }
  }, [revenuePeriod]);

  const handleSignOut = async () => { await signOut(); };

  const handleCreateClient = async (clientData: Parameters<typeof createClient>[0]) => {
    try {
      await createClient(clientData);
      toast.success('Client created successfully!');
      await loadClients();
    } catch (error) {
      toast.error('Failed to create client. Please try again.');
      throw error;
    }
  };

  const isTestAccount = (client: Client) => {
    const email = client.email.toLowerCase();
    return email.includes('test') || email.includes('example') || client.plan.toLowerCase().includes('test');
  };

  const realClients = clients.filter(c => !isTestAccount(c));
  const totalClients = clients.length;
  const activeClients = getActiveClients(realClients);
  const expiringClients = getExpiringClients(realClients);
  const activeLifetimeClients = getActiveLifetimeClients(realClients);
  const activeMonthlyClients = getActiveMonthlyClients(realClients);
  const testClients = clients.filter(c => isTestAccount(c));

  // Period-filtered supplementary data
  const now = new Date();
  let periodStart: Date;
  switch (revenuePeriod) {
    case 'month': periodStart = new Date(now.getFullYear(), now.getMonth(), 1); break;
    case '3months': periodStart = new Date(now.getFullYear(), now.getMonth() - 2, 1); break;
    case '6months': periodStart = new Date(now.getFullYear(), now.getMonth() - 5, 1); break;
    case 'year': periodStart = new Date(now.getFullYear(), 0, 1); break;
  }

  const newFreemiums = realClients.filter(c =>
    (c.plan.toLowerCase() === 'freemium' || (!c.paid && !c.amount_paid)) &&
    new Date(c.created_at) >= periodStart
  ).length;

  const periodAiCost = aiUsageLogs
    .filter(l => new Date(l.date) >= periodStart)
    .reduce((sum, l) => sum + l.credits_used, 0) * COST_PER_CREDIT;

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white relative overflow-hidden">
      <LightBackground variant="subtle" />

      {/* Header */}
      <motion.header
        className="relative z-20 p-3 sm:p-6 border-b border-border bg-white/80 backdrop-blur-sm"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-2 sm:gap-4">
            <Link to="/" className="flex items-center">
              <span className="text-lg sm:text-xl font-semibold text-foreground">
                Faceless Aura <span className="text-primary">Club</span>
              </span>
            </Link>
            <div className="h-4 sm:h-6 w-px bg-border" />
            <span className="text-xs sm:text-sm font-medium text-primary">Owner HQ</span>
            {useMockData && (
              <span className="text-[10px] sm:text-xs px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full bg-amber-100 text-amber-700 border border-amber-200">
                Demo
              </span>
            )}
          </div>
          <div className="flex items-center gap-1 sm:gap-4">
            {expiringClients.length > 0 && (
              <div className="hidden sm:flex items-center gap-2 px-3 py-1 rounded-full bg-amber-50 border border-amber-200">
                <AlertTriangle className="w-4 h-4 text-amber-600" />
                <span className="text-xs text-amber-700">{expiringClients.length} expiring</span>
              </div>
            )}
            <Button variant="ghost" size="icon" className="text-muted-foreground hover:text-foreground h-8 w-8 sm:h-10 sm:w-10"
              onClick={() => { loadClients(); loadFinancialData(); loadStripeRevenue(revenuePeriod); }}>
              <RefreshCw className="w-4 h-4 sm:w-5 sm:h-5" />
            </Button>
            <Link to="/admin/settings">
              <Button variant="ghost" size="icon" className="text-muted-foreground hover:text-foreground h-8 w-8 sm:h-10 sm:w-10">
                <Settings className="w-4 h-4 sm:w-5 sm:h-5" />
              </Button>
            </Link>
            <div className="hidden sm:block h-6 w-px bg-border" />
            <span className="hidden md:block text-sm text-muted-foreground truncate max-w-[150px]">{user?.email}</span>
            <Button variant="ghost" size="sm" className="text-muted-foreground hover:text-primary text-xs sm:text-sm px-2 sm:px-3" onClick={handleSignOut}>
              <span className="hidden sm:inline">Sign Out</span>
              <span className="sm:hidden">Exit</span>
            </Button>
          </div>
        </div>
      </motion.header>

      {/* Main content */}
      <div className="relative z-10 max-w-7xl mx-auto px-3 sm:px-6 py-4 sm:py-8">
        {/* Title */}
        <motion.div className="mb-4 sm:mb-6" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.1 }}>
          <h1 className="text-xl sm:text-2xl md:text-3xl font-semibold tracking-tight mb-1">
            <span className="text-foreground">Owner Command Center</span>
          </h1>
          <p className="text-muted-foreground text-xs sm:text-sm">
            Complete business overview · Last updated: {new Date().toLocaleTimeString()}
          </p>
        </motion.div>

        {/* Quick Actions */}
        <motion.div
          className="grid grid-cols-4 sm:grid-cols-8 gap-2 sm:gap-3 mb-4 sm:mb-6"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.15 }}
        >
          {[
            { to: '/admin/crm', icon: Database, label: 'CRM', color: 'text-primary' },
            { to: '/admin/content', icon: Eye, label: 'Content', color: 'text-primary' },
            { to: '/admin/analytics', icon: DollarSign, label: 'Analytics', color: 'text-green-600' },
            { to: '/admin/affiliates', icon: Users, label: 'Affiliates', color: 'text-primary' },
            { to: '/admin/support', icon: MessageCircle, label: 'Support', color: 'text-primary' },
            { to: '/admin/academies', icon: BookOpen, label: 'Academies', color: 'text-primary' },
            { to: '/admin/ai-tools', icon: Sparkles, label: 'AI Tools', color: 'text-amber-500' },
            { to: '/admin/launch-kit-briefs', icon: Rocket, label: 'Launch Kit', color: 'text-primary' },
          ].map(item => (
            <Link key={item.to} to={item.to} className="w-full">
              <Button variant="outline" className="w-full h-auto py-2.5 sm:py-3 flex flex-col gap-1 bg-white hover:bg-slate-50 border-border rounded-xl">
                <item.icon className={`w-4 h-4 ${item.color}`} />
                <span className="text-[9px] sm:text-[10px]">{item.label}</span>
              </Button>
            </Link>
          ))}
          <Button variant="outline" className="w-full h-auto py-2.5 sm:py-3 flex flex-col gap-1 bg-white hover:bg-slate-50 border-border rounded-xl"
            onClick={() => setShowCreateDialog(true)}>
            <Plus className="w-4 h-4 text-primary" />
            <span className="text-[9px] sm:text-[10px]">New</span>
          </Button>
        </motion.div>

        {/* KPI Row */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.2 }} className="mb-4 sm:mb-6">
          <OwnerKPIs 
            clients={clients}
            stripeData={stripeData}
            periodAiCost={periodAiCost}
          />
        </motion.div>

        {/* Revenue + Client Overview Row */}
        <motion.div
          className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-6 mb-4 sm:mb-6"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.25 }}
        >
          <RevenueBreakdown 
            stripeData={stripeData}
            stripeLoading={stripeLoading}
            stripeError={stripeError}
            newFreemiums={newFreemiums}
            periodAiCost={periodAiCost}
            revenuePeriod={revenuePeriod}
            onPeriodChange={setRevenuePeriod}
          />

          {/* Client Overview */}
          <div className="p-5 sm:p-6 border border-border bg-white rounded-2xl shadow-sm">
            <div className="flex items-center gap-2 mb-5">
              <div className="w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center">
                <Users className="w-4 h-4 text-primary" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-foreground">Client Overview</h3>
                <p className="text-[10px] text-muted-foreground">{totalClients} total records</p>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-2 sm:gap-3">
              {[
                { value: activeClients.length, label: 'Active (All)', sublabel: 'Monthly + Lifetime', bg: 'bg-green-50', border: 'border-green-100', color: 'text-green-600' },
                { value: activeMonthlyClients.length, label: 'Monthly Active', bg: 'bg-blue-50', border: 'border-blue-100', color: 'text-blue-600' },
                { value: activeLifetimeClients.length, label: 'Lifetime', bg: 'bg-purple-50', border: 'border-purple-100', color: 'text-purple-600' },
                { value: realClients.filter(c => c.plan.toLowerCase() === 'freemium').length, label: 'Freemiums', sublabel: 'Registrations only', bg: 'bg-slate-50', border: 'border-border', color: 'text-slate-600' },
                { value: realClients.filter(c => c.status === 'expired' || (c.paid && new Date(c.subscription_end) < new Date())).length, label: 'Expired / Off', bg: 'bg-red-50', border: 'border-red-100', color: 'text-red-600' },
                { value: testClients.length, label: 'Test Accounts', bg: 'bg-slate-50', border: 'border-border', color: 'text-slate-400' },
              ].map((stat, i) => (
                <div key={i} className={`p-2.5 sm:p-3 ${stat.bg} rounded-xl border ${stat.border}`}>
                  <p className={`text-lg sm:text-xl font-bold ${stat.color}`}>{stat.value}</p>
                  <p className="text-[9px] sm:text-[10px] text-muted-foreground mt-0.5">{stat.label}</p>
                  {stat.sublabel && <p className="text-[8px] sm:text-[9px] text-muted-foreground/60">{stat.sublabel}</p>}
                </div>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Activity Feed + Alerts Row */}
        <motion.div
          className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-6"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          <ActivityFeed />
          <OwnerAlerts clients={clients} />
        </motion.div>
      </div>

      <CreateClientDialog
        isOpen={showCreateDialog}
        onClose={() => setShowCreateDialog(false)}
        onSubmit={handleCreateClient}
      />
    </div>
  );
};

export default AdminDashboard;
